import { Head, Link } from '@inertiajs/react';

/**
 * Book data from the backend.
 */
interface TomeBook {
    id: number;
    title: string;
    slug: string;
    description: string | null;
    hue: number;
    cover_image_url: string | null;
    is_published: boolean;
}

/**
 * Page data for table of contents.
 */
interface TomePage {
    id: number;
    name: string;
    slug: string;
    is_published: boolean;
}

/**
 * Chapter data with pages.
 */
interface TomeChapter {
    id: number;
    title: string;
    description: string | null;
    pages: TomePage[];
}

/**
 * Props for the Book page.
 */
interface Props {
    book: TomeBook;
    chapters: TomeChapter[];
    isGm: boolean;
}

/**
 * Public Book view - displays a book's chapters and table of contents.
 */
export default function Book({ book, chapters, isGm }: Props) {
    // Generate colors from hue
    const accentColor = `hsl(${book.hue}, 50%, 40%)`;
    const accentColorLight = `hsl(${book.hue}, 60%, 50%)`;

    // Count total pages
    const totalPages = chapters.reduce((sum, ch) => sum + ch.pages.length, 0);

    return (
        <>
            <Head title={`${book.title} - Archivist's Tome`} />

            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
                {/* Header with book accent color */}
                <header
                    className="border-b"
                    style={{
                        backgroundColor: `${accentColor}20`,
                        borderColor: accentColor,
                    }}
                >
                    <div className="max-w-4xl mx-auto px-4 py-6">
                        {/* Breadcrumb */}
                        <div className="flex items-center gap-2 text-sm mb-4">
                            <Link href="/tome" className="text-amber-200/60 hover:text-amber-200">
                                📚 Bookshelf
                            </Link>
                            <span className="text-amber-200/40">→</span>
                            <span style={{ color: accentColorLight }}>{book.title}</span>
                        </div>

                        <div className="flex items-start justify-between">
                            <div>
                                <h1 className="text-3xl font-serif text-amber-200 flex items-center gap-3">
                                    📖 {book.title}
                                </h1>
                                {book.description && (
                                    <p className="text-amber-200/70 mt-2 max-w-2xl">
                                        {book.description}
                                    </p>
                                )}
                                <p className="text-amber-200/50 text-sm mt-2">
                                    {chapters.length} chapter{chapters.length !== 1 ? 's' : ''} • {totalPages} page{totalPages !== 1 ? 's' : ''}
                                </p>
                            </div>

                            {/* Draft indicator for GMs */}
                            {isGm && !book.is_published && (
                                <span className="px-3 py-1 bg-yellow-600 text-white text-sm rounded">
                                    Draft
                                </span>
                            )}
                        </div>
                    </div>
                </header>

                {/* Table of Contents */}
                <main className="max-w-4xl mx-auto px-4 py-8">
                    {chapters.length === 0 ? (
                        <EmptyBook />
                    ) : (
                        <div className="space-y-8">
                            {chapters.map((chapter) => (
                                <ChapterSection
                                    key={chapter.id}
                                    chapter={chapter}
                                    accentColor={accentColor}
                                    isGm={isGm}
                                />
                            ))}
                        </div>
                    )}
                </main>
            </div>
        </>
    );
}

/**
 * Empty state when book has no chapters.
 */
function EmptyBook() {
    return (
        <div className="text-center py-16">
            <div className="text-4xl mb-4">📄</div>
            <h2 className="text-xl font-serif text-amber-200 mb-2">
                This Book is Empty
            </h2>
            <p className="text-amber-200/60">
                The pages of this tome await their first words...
            </p>
        </div>
    );
}

/**
 * ChapterSection - Displays a chapter with its pages.
 */
function ChapterSection({ chapter, accentColor, isGm }: { chapter: TomeChapter; accentColor: string; isGm: boolean }) {
    return (
        <section>
            <h2
                className="text-xl font-serif mb-3 pb-2 border-b"
                style={{ color: accentColor, borderColor: `${accentColor}40` }}
            >
                {chapter.title}
            </h2>
            {chapter.description && (
                <p className="text-amber-200/60 text-sm mb-4">{chapter.description}</p>
            )}
            <ul className="space-y-2">
                {chapter.pages.map((page) => (
                    <li key={page.id}>
                        <Link
                            href={`/tome/page/${page.slug}`}
                            className="flex items-center gap-2 px-4 py-2 rounded bg-slate-800/50 hover:bg-slate-700/50 text-amber-200/80 hover:text-amber-100 transition-colors"
                        >
                            <span className="text-amber-200/40">📄</span>
                            <span>{page.name}</span>
                            {isGm && !page.is_published && (
                                <span className="ml-auto px-2 py-0.5 bg-yellow-600/50 text-yellow-200 text-xs rounded">Draft</span>
                            )}
                        </Link>
                    </li>
                ))}
            </ul>
        </section>
    );
}

